import React, { useState } from 'react';
import { Users, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import TagsEditor from './TagsEditor';

export default function CoordinatingSettingsEditor({ settings = [], onChange }) {
  // Безопасная обработка данных
  const coordinatingSettings = Array.isArray(settings) ? settings : [];
  
  // Раскрываем все настройки по умолчанию
  const initialExpanded = {};
  coordinatingSettings.forEach((_, index) => {
    initialExpanded[index] = true;
  });
  
  const [expandedSettings, setExpandedSettings] = useState(initialExpanded);

  const addSetting = () => {
    const newSettings = [...coordinatingSettings, {
      Manager_ID: coordinatingSettings.length + 1,
      If_Have_Tags: '',
      Coordination_Tag: '',
      If_Not_Have_Tags: '',
      Instruction_For_Task: '',
      Coordination_Info_For_Manager: ''
    }];
    // Автоматически раскрываем новую настройку
    setExpandedSettings(prev => ({ ...prev, [coordinatingSettings.length]: true }));
    onChange(newSettings);
  };

  const updateSetting = (index, field, value) => {
    const newSettings = [...coordinatingSettings];
    newSettings[index][field] = value;
    onChange(newSettings);
  };

  const deleteSetting = (index) => {
    if (!window.confirm('Удалить настройку координации?')) return;
    const newSettings = coordinatingSettings.filter((_, i) => i !== index);
    onChange(newSettings);
  };

  const toggleExpand = (index) => {
    setExpandedSettings(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Users className="text-purple-600" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Настройки координации</h3>
            <p className="text-sm text-gray-600">Параметры передачи контроля менеджерам</p>
          </div>
        </div>
        <Button onClick={addSetting} size="sm">
          <Plus size={16} className="mr-1" />
          Добавить настройку
        </Button>
      </div>

      <div className="space-y-4">
        <AnimatePresence>
          {coordinatingSettings.map((setting, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white border-2 border-purple-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <button
                  onClick={() => toggleExpand(index)}
                  className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors"
                >
                  {expandedSettings[index] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <span className="font-medium">
                    Настройка координации для менеджера #{setting.Manager_ID}
                  </span>
                </button>
                <button
                  onClick={() => deleteSetting(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Удалить настройку"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              {expandedSettings[index] && (
                <div className="space-y-4 pl-7 border-l-2 border-purple-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ID менеджера
                    </label>
                    <input
                      type="number"
                      value={setting.Manager_ID || ''}
                      onChange={(e) => updateSetting(index, 'Manager_ID', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Теги включения (If_Have_Tags)
                    </label>
                    <input
                      type="text"
                      value={setting.If_Have_Tags || ''}
                      onChange={(e) => updateSetting(index, 'If_Have_Tags', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="tag1,tag2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Теги исключения (If_Not_Have_Tags)
                    </label>
                    <input
                      type="text"
                      value={setting.If_Not_Have_Tags || ''}
                      onChange={(e) => updateSetting(index, 'If_Not_Have_Tags', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="tag3,tag4"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Тег координации (Coordination_Tag)
                    </label>
                    <input
                      type="text"
                      value={setting.Coordination_Tag || ''}
                      onChange={(e) => updateSetting(index, 'Coordination_Tag', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="manager_call"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Инструкция для задачи (Instruction_For_Task)
                    </label>
                    <textarea
                      value={setting.Instruction_For_Task || ''}
                      onChange={(e) => updateSetting(index, 'Instruction_For_Task', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows={3}
                      placeholder="Что передать боту для следующего шага"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Информация для менеджера (Coordination_Info_For_Manager)
                    </label>
                    <textarea
                      value={setting.Coordination_Info_For_Manager || ''}
                      onChange={(e) => updateSetting(index, 'Coordination_Info_For_Manager', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows={3}
                      placeholder="Саммари диалога и текущий статус клиента"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {coordinatingSettings.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white border-2 border-dashed border-gray-300 rounded-xl">
          <Users className="mx-auto mb-4 text-gray-400" size={48} />
          <p>Нет настроек координации. Добавьте первую настройку для координации с менеджерами.</p>
        </div>
      )}
    </div>
  );
}
